import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
    Typography,
    Tooltip,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import api, { apiErrorMessage as errorMessage } from "../../service/api";
import dataforms from "../../service/dataforms";
import { getTenant } from "../../libs/tenant";

// Gerencia os botões de ação (status) de um formulário: criar, renomear e excluir.
// Automação opcional por botão: ao clicar, cria em segundo plano um registro de
// outro formulário (`target_form_id`) já vinculado ao preenchimento — o
// formulário de destino precisa de um "Vínculo de Formulário" apontando para
// este (o backend valida).
export default function ActionFormDialog({ open, onClose, formId }) {
    const [actions, setActions] = useState([]);
    const [forms, setForms] = useState([]);
    const [newAction, setNewAction] = useState("");
    const [newTarget, setNewTarget] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [editingTarget, setEditingTarget] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);

    // O dialog fica montado mesmo fechado, com formId nulo.
    const currentFormId = formId ? formId.id : null;
    const baseUrl = `action-forms/${currentFormId}/actions`;

    useEffect(() => {
        if (!open || !currentFormId) return;
        setNewAction("");
        setNewTarget("");
        setEditingId(null);
        setConfirmDelete(null);
        setError("");
        setLoading(true);
        api.get(`action-forms/${currentFormId}/actions`)
            .then((res) => setActions(res.data || []))
            .catch(() => setError("Não foi possível carregar os botões."))
            .finally(() => setLoading(false));
        dataforms
            .listForms(getTenant())
            .then((list) => setForms(list || []))
            .catch(() => setForms([]));
    }, [open, currentFormId]);

    // Destinos possíveis: os outros formulários da empresa.
    const targetOptions = forms.filter((f) => String(f.id) !== String(currentFormId));

    const renderTargetSelect = (value, onChange) => (
        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
            <InputLabel>Ao clicar, criar registro em</InputLabel>
            <Select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                label="Ao clicar, criar registro em"
            >
                <MenuItem value="">
                    <em>Nada (só registra a ação)</em>
                </MenuItem>
                {targetOptions.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                        {f.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const handleAdd = async () => {
        const name = newAction.trim();
        if (!name) return;
        setSaving(true);
        setError("");
        try {
            const res = await api.post(baseUrl, {
                name,
                target_form_id: newTarget || null,
            });
            setActions((prev) => [...prev, res.data]);
            setNewAction("");
            setNewTarget("");
        } catch (err) {
            setError(errorMessage(err, "Não foi possível criar o botão."));
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (action) => {
        setEditingId(action.id);
        setEditingName(action.name);
        setEditingTarget(action.target_form_id || "");
        setConfirmDelete(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingName("");
        setEditingTarget("");
    };

    const handleUpdate = async () => {
        const name = editingName.trim();
        if (!name) return;
        setSaving(true);
        setError("");
        try {
            const res = await api.put(`${baseUrl}/${editingId}`, {
                name,
                target_form_id: editingTarget || null,
            });
            setActions((prev) =>
                prev.map((a) => (a.id === editingId ? { ...a, ...res.data } : a))
            );
            cancelEdit();
        } catch (err) {
            setError(errorMessage(err, "Não foi possível salvar o botão."));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (actionId) => {
        setSaving(true);
        setError("");
        try {
            await api.delete(`${baseUrl}/${actionId}`);
            setActions((prev) => prev.filter((a) => a.id !== actionId));
            setConfirmDelete(null);
        } catch (err) {
            setError("Não foi possível excluir o botão.");
        } finally {
            setSaving(false);
        }
    };

    const renderItem = (action) => {
        if (editingId === action.id) {
            return (
                <ListItem key={action.id} divider>
                    <div style={{ display: "flex", gap: 8, flex: 1, marginRight: 96, flexWrap: "wrap" }}>
                        <TextField
                            autoFocus
                            size="small"
                            variant="outlined"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleUpdate();
                                if (e.key === "Escape") cancelEdit();
                            }}
                            style={{ flex: 1, minWidth: 160 }}
                        />
                        {renderTargetSelect(editingTarget, setEditingTarget)}
                    </div>
                    <ListItemSecondaryAction>
                        <Tooltip title="Salvar">
                            <span>
                                <IconButton
                                    color="primary"
                                    disabled={saving || !editingName.trim()}
                                    onClick={handleUpdate}
                                >
                                    <CheckIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                            <IconButton edge="end" onClick={cancelEdit}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </ListItemSecondaryAction>
                </ListItem>
            );
        }

        if (confirmDelete === action.id) {
            return (
                <ListItem key={action.id} divider>
                    <ListItemText
                        primary={`Excluir "${action.name}"?`}
                        secondary="Os registros desse botão nos preenchimentos também serão removidos."
                        style={{ marginRight: 180 }}
                    />
                    <ListItemSecondaryAction>
                        <Button size="small" onClick={() => setConfirmDelete(null)}>
                            Cancelar
                        </Button>
                        <Button
                            size="small"
                            color="secondary"
                            disabled={saving}
                            onClick={() => handleDelete(action.id)}
                        >
                            Excluir
                        </Button>
                    </ListItemSecondaryAction>
                </ListItem>
            );
        }

        return (
            <ListItem key={action.id} divider>
                <ListItemText
                    primary={action.name}
                    secondary={
                        action.target_form_id
                            ? `→ cria registro em ${
                                  (action.target_form && action.target_form.name) ||
                                  `formulário #${action.target_form_id}`
                              }`
                            : null
                    }
                />
                <ListItemSecondaryAction>
                    <Tooltip title="Editar">
                        <IconButton disabled={saving} onClick={() => startEdit(action)}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                        <IconButton
                            edge="end"
                            disabled={saving}
                            onClick={() => {
                                cancelEdit();
                                setConfirmDelete(action.id);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </ListItemSecondaryAction>
            </ListItem>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Gerenciar Botões do Formulário
                {formId && formId.name && (
                    <Typography variant="body2" color="textSecondary">
                        {formId.name}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent dividers>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <TextField
                        label="Novo botão"
                        placeholder="Ex.: Aprovar, Reprovar, Concluir"
                        variant="outlined"
                        size="small"
                        value={newAction}
                        onChange={(e) => setNewAction(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleAdd();
                        }}
                        style={{ flex: 1, minWidth: 180 }}
                    />
                    {renderTargetSelect(newTarget, setNewTarget)}
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={saving || !newAction.trim()}
                        onClick={handleAdd}
                    >
                        Adicionar
                    </Button>
                </div>

                {error && (
                    <Typography
                        variant="body2"
                        color="error"
                        style={{ marginTop: 12 }}
                    >
                        {error}
                    </Typography>
                )}

                <Divider style={{ marginTop: 16 }} />

                {loading ? (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <CircularProgress size={28} />
                    </div>
                ) : actions.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ padding: "24px 0", textAlign: "center" }}
                    >
                        Nenhum botão cadastrado para este formulário.
                    </Typography>
                ) : (
                    <List dense>{actions.map(renderItem)}</List>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
